import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  computed,
  signal,
  effect,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { formatDateShort } from "@core/utils/date.util";
import { getPdfUrl } from "@core/utils/url";
import { environment } from "@env/environment";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { Invoice } from "../../models/invoice.models";
import { InvoiceResendEmailService } from "../../services/invoice-resend-email.service";
import { InvoiceService } from "../../services/invoice.service";
import { Voucher } from "@features/order-actions/models/voucher.model";
import { formatSoles } from "@core/utils/money.util";
import { HorusLoadingComponent } from "@shared/components/domain/horus-loading/horus-loading.component";
import { HorusMessageGeneralComponent } from "@shared/components/domain/horus-message-general/horus-message-general.component";
import { getDocumentType } from "@core/helpers/document-type";
import { VouchersState } from "../../store/vouchers.state";

type InvoiceGetData = {
  seller: string;
  orderId: string;
  tenantId: string;
  date: Date;
  email: string;
};

interface InvoiceDrawerData extends Partial<Invoice> {
  close?: () => void;
  invoiceGetData?: InvoiceGetData;
  filterCreditNotes?: boolean;
}

@Component({
  selector: "app-invoice-drawer",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    HorusLoadingComponent,
    HorusMessageGeneralComponent,
  ],
  template: `
    <div class="w-full h-full bg-white flex flex-col font-['Geometria']">
      <div class="flex items-center justify-between px-3 pr-4 py-1 line-bottom">
        <h2 class="title">Comprobante de pago</h2>
        <button
          data-testid="invoice-drawer-close-btn icon"
          (click)="onClose()"
          class="bg-white outline-none border-none text-[#0C6AA8] w-[16px]"
        >
          <mat-icon
            style="display: flex; justify-content: center; align-items: center;"
            class=" text-[#0C6AA8] text-[20px]"
            fontSet="material-icons-outlined"
            >close</mat-icon
          >
        </button>
      </div>

      @if (vouchersQuery().isLoading) {
        <div
          class="flex-1 flex items-start justify-center mt-8 max-w-[230px] mx-auto"
        >
          <app-horus-loading [size]="'S'"></app-horus-loading>
        </div>
      } @else if (vouchersQuery().isError) {
        <div
          class="flex-1 flex items-start justify-center mt-8 max-w-[230px] mx-auto"
        >
          <app-horus-message-general
            nameIcon="errorMessageIcon"
            btnAction="INTENTAR NUEVAMENTE"
            (action)="retryQuery()"
          >
            <span style="color:#F14B26; font-size: 14px; line-height: 25px;"
              >Lo sentimos</span
            ><br />
            Hubo un <strong>error al intentar cargar</strong> el comprobante de
            pago.
          </app-horus-message-general>
        </div>
      } @else if (!currentInvoice() && vouchersQuery().data) {
        <div
          class="flex-1 flex items-start justify-center mt-8 max-w-[230px] mx-auto"
        >
          <app-horus-message-general
            nameIcon="noDataMessageIcon"
            btnAction="ENTENDIDO"
            (action)="onClose()"
          >
            <span style="color:#F6D44D; font-size: 14px; line-height: 25px;"
              >No podemos ejecutar la acción</span
            ><br />
            Este pedido
            <strong>no tiene un comprobante de pago disponible.</strong>
          </app-horus-message-general>
        </div>
      } @else if (currentInvoice()) {
        <div class="flex-1 overflow-y-auto mt-2">
          <div class="flex justify-between px-5 items-center">
            <h3
              class="text-[#064C79] font-bold bg-[#EEE] pr-4 pl-1 py-1 text-lg text-[15px] rounded-[4px]"
            >
              {{ currentInvoice()?.orderId }}
            </h3>
            <mat-icon
              (click)="onViewPDF()"
              class="!bg-[#FBCF50] w-[24px] cursor-pointer text-[14px] rounded-[8.889px]  text-white flex justify-center items-center"
              fontSet="material-icons-outlined"
              >remove_red_eye</mat-icon
            >
          </div>
          <div
            class="px-2 mx-4 border-[#E7F5FE] border-[1px] border-solid text-[11px] rounded-[4px] -mt-2 pr-4"
          >
            <div class="grid grid-cols-2 gap-x-4">
              <div>
                <p class="text-[#0C6AA8]">Serie folio</p>
                <p class="text font-medium -mt-2 ">
                  {{ getSerieFromFolio() }}
                </p>
              </div>
              <div>
                <p class="text-[#0C6AA8] leading-5">Tipo comprobante</p>
                <p class="text font-medium -mt-3">
                  {{ getDocumentTypeForDisplay() }}
                </p>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-x-4 -mt-3">
              <div>
                <p class="text-[#0C6AA8]">Fecha de emisión</p>
                <p class="text -mt-2">
                  {{ getFormattedDate(currentInvoice()?.date) }}
                </p>
              </div>
              <div>
                <p class="text-[#0C6AA8]">Monto total</p>
                <p class="text  font-medium -mt-2">
                  {{ currentInvoice()?.amount }}
                </p>
              </div>
            </div>
            @if (tenantInfo() && tenantInfo().name) {
              <div class="grid grid-cols-2 gap-x-4 -mt-3">
                <div>
                  <p class="text-[#0C6AA8]">Vendido por</p>
                  <p class="text -mt-2">
                    <span class="flex items-center space-x-1">
                      <mat-icon
                        [style]="'color: ' + (tenantInfo().color || '#000000')"
                        class="text-[12px] -mb-2 -mr-2"
                        >circle</mat-icon
                      >
                      <span>
                        {{ tenantInfo().name }}
                      </span>
                    </span>
                  </p>
                </div>
              </div>
            }
          </div>
        </div>
        <div class="px-6 py-4 space-y-2">
          <button
            mat-flat-button
            class="w-full"
            color="primary"
            data-testid="invoice-drawer-send-button"
            (click)="onSendClick()"
            [disabled]="vouchersQuery().isLoading"
          >
            ENVIAR COMPROBANTE DE PAGO
          </button>
          <button
            mat-stroked-button
            class="w-full"
            color="primary"
            data-testid="invoice-drawer-cancel-button"
            (click)="onClose()"
          >
            CANCELAR
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }

      .text {
        color: #636363;
        font-family: Geometria;
        font-size: 11px;
        font-style: normal;
        font-weight: 500;
        line-height: 16px; /* 145.455% */
      }
      .icon {
        width: 20px;
        height: 20px;
        display: flex;
      }

      .title {
        color: var(--Primary-Blue-500, #0e98f0);
        font-family: Geometria;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 18px;
      }

      .line-bottom {
        border-bottom: 1px solid #f7f7f7;
      }
      ::ng-deep .mat-mdc-icon-button.mat-mdc-button-base {
        width: 48px !important;
        height: 48px !important;
        padding: 0 !important;
      }

      mat-icon {
        display: inline-flex !important;
      }
    `,
  ],
})
export class InvoiceDrawerComponent {
  private invoiceService = inject(InvoiceService);
  private invoiceResendEmailService = inject(InvoiceResendEmailService);
  private tenantUrlUtil = inject(TenantUrlUtilService);
  private vouchersState = inject(VouchersState);

  invoiceData: InvoiceDrawerData = inject(MAT_DIALOG_DATA);

  tenantInfo = signal<any>(null);

  constructor() {
    effect(() => {
      const vouchers = this.vouchersQuery()?.data;
      if (vouchers && vouchers.length > 0 && this.invoiceData.invoiceGetData) {
        this.tenantInfo.set({
          name: this.invoiceData.invoiceGetData.seller,
          color:
            environment.tenants.find(
              (t) => t.tenantId === this.invoiceData.invoiceGetData?.tenantId,
            )?.tenantColor || "#000000",
        });
      }
    });
  }

  vouchersQuery = this.vouchersState.getVouchersQuery(
    this.invoiceData.invoiceGetData?.tenantId,
    this.invoiceData.invoiceGetData?.orderId
  ).result;

  private mapVoucherToInvoice(voucher: Voucher): Invoice {
    return {
      amount: formatSoles(voucher.amount),
      date: formatDateShort(voucher.documentDate),
      documentType:
        (getDocumentType(voucher.documentType).type as string) || "boleta",
      folio: voucher.documentNumber,
      documentTypeLabel:
        (getDocumentType(voucher.documentType).label as string) || "Boleta",
      documentTypeId: 3,
      rucEmpresa: "",
      invoicePdfUrl: voucher.documentURL,
      receiptType: "virtual",
      receiptTypeLabel: "Virtual",
      location: "----",
      orderId: voucher.orderId,
    };
  }

  currentInvoice = computed(() => {
    if (!this.invoiceData.invoiceGetData) {
      return this.invoiceData as Invoice;
    }
    const query = this.vouchersQuery();
    const vouchers = query?.data;

    if (!vouchers || vouchers.length === 0) {
      return null;
    }

    let filteredVouchers = vouchers;
    if (this.invoiceData.filterCreditNotes) {
      filteredVouchers = this.vouchersState.filterCreditNotes(vouchers);
    }

    if (filteredVouchers.length === 0) {
      return null;
    }

    return this.mapVoucherToInvoice(filteredVouchers[0]);
  });

  onClose(): void {
    if (this.invoiceData.close) {
      this.invoiceData.close();
    }
  }

  getDocumentTypeForDisplay(): string {
    const invoice = this.currentInvoice();
    return invoice?.documentTypeLabel || "";
  }

  getFormattedDate(dateString: string | undefined): string {
    if (!dateString) return "";
    return formatDateShort(dateString);
  }

  getSerieFromFolio(): string {
    const invoice = this.currentInvoice();

    if (!invoice?.folio) return "";
    return invoice.folio;
  }

  getTenantFromLocation(): string {
    if (this.invoiceData.invoiceGetData) {
      return this.invoiceData.invoiceGetData.seller;
    }
    return "";
  }

  onViewPDF(): void {
    const invoice = this.currentInvoice();
    if (!invoice) return;

    if (invoice.invoicePdfUrl) {
      window.open(getPdfUrl(invoice.invoicePdfUrl));
      return;
    }

    const tenantId = this.tenantUrlUtil.getTenantId();

    if (!tenantId) {
      console.error("No tenant ID found");
      return;
    }

    const tenantInfo = environment.tenants.find(
      (t) => t.tenantId === tenantId.toUpperCase(),
    );

    if (!tenantInfo || !tenantInfo.ruc) {
      console.error("No RUC found for tenant:", tenantId);
      return;
    }

    this.invoiceService
      .getVoucherPDF(tenantId, {
        documentTypeId: String(invoice.documentTypeId),
        ruc: invoice.rucEmpresa!,
        serieFolio: invoice.folio,
      })
      .subscribe({
        next: (result) => {
          window.open(getPdfUrl(result.data.url));
        },
        error: (error) => {
          console.error("Error fetching PDF:", error);
        },
      });
  }

  onSendClick(): void {
    const invoice = this.currentInvoice();
    if (invoice) {
      const dialogRef =
        this.invoiceResendEmailService.openResendEmailModal(invoice);

      // Subscribe to modal close event to close drawer on success
      dialogRef.afterClosed().subscribe((result: any) => {
        if (result === true) {
          // Modal closed with success (email sent), close drawer too
          this.onClose();
        }
      });
    }
  }

  retryQuery(): void {
    this.vouchersQuery().refetch();
  }
}
